import { useCallback, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { ContentTypes } from "../../constants/action-types";
import { search } from "../../api/ProjectApi";
import DealIcon from "../../icons/svg/deals.svg?react";
import ContactIcon from "../../icons/svg/people.svg?react";
import CompanyIcon from "../../icons/svg/companies.svg?react";
import { RecordBadge } from "../../main/component/picker-shared";

interface SelectedItemValue {
  type: string;
  value?: string;
  label?: string;
}

interface ToolbarItemPickerProps {
  selectedItem?: SelectedItemValue;
  onSelectItem: (item: any) => void;
  isDisabled?: boolean;
}

const TYPE_OPTIONS = [
  { value: ContentTypes.SALE, labelKey: "Deal" },
  { value: ContentTypes.CONTACT, labelKey: "Contact" },
  { value: ContentTypes.COMPANY, labelKey: "Account" },
];

const TypeIcon = ({ type, className = "w-4 h-4" }: { type: string; className?: string }) => {
  if (type === ContentTypes.CONTACT) {
    return <RecordBadge Icon={ContactIcon} shape="square" className={className} />;
  }
  if (type === ContentTypes.COMPANY) {
    return <RecordBadge Icon={CompanyIcon} shape="square" className={className} />;
  }
  return <RecordBadge Icon={DealIcon} shape="square" className={className} />;
};

const ToolbarItemPicker = ({
  selectedItem,
  onSelectItem,
  isDisabled = false,
}: ToolbarItemPickerProps) => {
  const { t } = useTranslation();
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const projectId = project.get("id");

  const itemType = selectedItem?.type || ContentTypes.SALE;
  const hasSelection = !!selectedItem?.value;

  const [open, setOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTypeMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search items when query changes
  const doSearch = useCallback(
    (q: string) => {
      setLoading(true);
      search({
        projectId,
        pageSize: 6,
        types: [itemType],
        filters: q ? { name: { operator: "regex", values: [q] } } : {},
      })
        .then((result) => {
          const items = fromJS(result)
            .getIn(["pageResult", "content"])
            .toJS()
            .map((item: any) => ({
              id: item.id,
              label: item.data?.name?.value || "",
              type: item.type,
              raw: item,
            }));
          setResults(items);
        })
        .finally(() => setLoading(false));
    },
    [projectId, itemType]
  );

  useEffect(() => {
    if (open) {
      doSearch(query);
    }
  }, [itemType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenSearch = () => {
    if (isDisabled) return;
    setOpen(true);
    setQuery("");
    doSearch("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelectType = (type: string) => {
    setTypeMenuOpen(false);
    onSelectItem(fromJS({ type }));
    setQuery("");
    doSearch("");
  };

  const handleSelectResult = (item: any) => {
    onSelectItem(fromJS(item.raw));
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectItem(fromJS({ type: itemType }));
  };

  const currentTypeLabel = TYPE_OPTIONS.find((o) => o.value === itemType)?.labelKey || "Deal";

  return (
    <div
      ref={containerRef}
      className="relative flex items-center"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Pill trigger */}
      <div
        className={`flex items-center h-[26px] rounded-md border text-xs font-medium transition-colors select-none
          ${
            isDisabled
              ? "bg-gray-50 border-gray-200 text-gray-400 cursor-default"
              : "bg-white border-gray-300 text-gray-700 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50"
          }
          ${open || hasSelection ? "border-blue-400 bg-blue-50/60" : ""}
        `}
      >
        {/* Type selector button */}
        <div
          className={`flex items-center gap-1 px-2 h-full border-r transition-colors
            ${isDisabled ? "cursor-default text-gray-400" : "hover:text-blue-600 cursor-pointer"}
            ${typeMenuOpen ? "text-blue-600" : ""}
            border-gray-200
          `}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled) {
              setTypeMenuOpen((v) => !v);
              setOpen(false);
            }
          }}
        >
          <TypeIcon type={itemType} className="w-4 h-4 shrink-0" />
          <span className="leading-none">{t(currentTypeLabel)}</span>
          <ChevronDown
            className={`w-3 h-3 shrink-0 transition-transform ${typeMenuOpen ? "rotate-180" : ""}`}
          />
        </div>

        {/* Selected value or search trigger */}
        <div
          className={`inline-flex items-center gap-1.5 px-2 h-full
            ${isDisabled ? "cursor-default text-gray-400" : "cursor-pointer"}
            ${hasSelection ? "text-blue-600 font-semibold" : "text-gray-400"}
          `}
          style={{ lineHeight: 1 }}
          onClick={handleOpenSearch}
        >
          {hasSelection ? (
            <span className="max-w-[120px] truncate text-xs block" style={{ lineHeight: 1 }}>
              {selectedItem?.label}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs" style={{ lineHeight: 1 }}>
              <Search className="w-3 h-3 shrink-0" style={{ display: "block" }} />
              <span style={{ lineHeight: 1 }}>{t("Search...")}</span>
            </span>
          )}
        </div>

        {/* Clear button */}
        {hasSelection && !isDisabled && (
          <div
            className="flex items-center justify-center w-5 h-full text-gray-400 hover:text-gray-600 mr-0.5 cursor-pointer"
            onClick={handleClear}
          >
            <X className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Type menu dropdown */}
      {typeMenuOpen && (
        <div className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
          {TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors
                ${
                  opt.value === itemType
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-gray-50"
                }
              `}
              onClick={() => handleSelectType(opt.value)}
            >
              <TypeIcon type={opt.value} className="w-4 h-4 shrink-0" />
              {t(opt.labelKey)}
            </div>
          ))}
        </div>
      )}

      {/* Search dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth: 240, maxWidth: 320, width: "max-content" }}
        >
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              placeholder={t("Search...")}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                doSearch(e.target.value);
              }}
            />
            {query && (
              <div
                className="cursor-pointer"
                onClick={() => {
                  setQuery("");
                  doSearch("");
                }}
              >
                <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
              </div>
            )}
          </div>

          {/* Results */}
          <div className="max-h-52 overflow-y-auto py-1 flex flex-col">
            {loading ? (
              <div className="px-3 py-2 text-xs text-gray-400">{t("Loading...")}</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">{t("No options")}</div>
            ) : (
              results.map((item) => (
                <div
                  key={item.id}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left cursor-pointer transition-colors hover:bg-gray-50"
                  onClick={() => handleSelectResult(item)}
                >
                  <TypeIcon type={item.type} className="w-4 h-4 shrink-0" />
                  <span
                    className="truncate font-medium text-gray-800 block flex-1 min-w-0"
                    title={item.label}
                  >
                    {item.label}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolbarItemPicker;
